<template>
  <div class="home">
    <section class="hero">
      <div class="hero-copy">
        <h1>Rentz Score</h1>
        <p class="hero-lede">
          Track scores for 3 to 6 players, across all eight games. Pick a game, enter what each
          player took, and the totals add up for you.
        </p>

        <div v-if="gameStore.hasActiveGame" class="resume">
          <p class="resume-summary">
            <strong>Game in progress</strong>
            <span>
              Round {{ gameStore.currentRound }} of {{ gameStore.maxRound }} with
              {{ playerList }}
            </span>
          </p>
          <div class="actions">
            <router-link to="/game" class="btn btn-primary">Resume game</router-link>
            <router-link to="/new-game" class="btn btn-secondary">New game</router-link>
          </div>
        </div>
        <div v-else class="actions">
          <router-link to="/new-game" class="btn btn-primary">New game</router-link>
          <router-link v-if="gameStore.history.length" to="/history" class="btn btn-secondary">
            Past games
          </router-link>
        </div>
      </div>

      <div class="fan" aria-hidden="true">
        <div
          v-for="(card, i) in fanCards"
          :key="card.rank + card.suit"
          class="fan-card"
          :style="{ '--i': i }"
        >
          <span class="corner" :class="{ red: card.red }">
            <span>{{ card.rank }}</span
            ><span>{{ card.suit }}</span>
          </span>
          <span class="pip" :class="{ red: card.red }">{{ card.suit }}</span>
          <span class="corner corner-bottom" :class="{ red: card.red }">
            <span>{{ card.rank }}</span
            ><span>{{ card.suit }}</span>
          </span>
        </div>
      </div>
    </section>

    <section class="rules" aria-labelledby="rules-title">
      <h2 id="rules-title">The eight games</h2>
      <p class="rules-lede">
        Each player picks every game once when it's their turn to choose. Points shown are for a
        {{ examplePlayers }}-player game.
      </p>
      <ul class="rules-grid">
        <li v-for="game in games" :key="game.id" class="rule">
          <GameIndex :symbol="game.symbol" />
          <span class="rule-text">
            <span class="rule-name">{{ game.displayName }}</span>
            <span class="rule-points">{{ ruleText(game, examplePlayers) }}</span>
          </span>
        </li>
      </ul>
    </section>

    <footer class="home-footer">
      <router-link to="/privacy">Privacy policy</router-link>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useGameStore } from '../stores/game';
import { useGameConfigStore } from '../stores/gameConfig';
import { ruleText } from '../utils/gameRules';
import GameIndex from '../components/GameIndex.vue';

const gameStore = useGameStore();
const gameConfig = useGameConfigStore();

const games = Object.values(gameConfig.games);
const examplePlayers = computed(() => (gameStore.hasActiveGame ? gameStore.players.length : 4));

const playerList = computed(() => {
  const names = gameStore.playerNames;
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
});

const fanCards = [
  { rank: 'Q', suit: '♠', red: false },
  { rank: '10', suit: '♣', red: false },
  { rank: 'A', suit: '♦', red: true },
  { rank: 'K', suit: '♥', red: true }
];
</script>

<style scoped>
.home {
  max-width: 1040px;
  margin: 0 auto;
}

.home-footer {
  margin-top: 2.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--table-line);
  font-size: var(--step--1);
}

.home-footer a {
  display: inline-block;
  padding: 0.5rem 0;
  color: var(--on-table-muted);
  text-underline-offset: 2px;
}

.hero {
  display: grid;
  gap: 2rem;
  align-items: center;
  padding-bottom: 2.5rem;
}

h1 {
  font-family: var(--font-display);
  font-size: clamp(2.25rem, 11vw, 4.5rem);
  white-space: nowrap;
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.01em;
}

.hero-lede {
  margin-top: 1rem;
  max-width: 30em;
  color: var(--on-table-muted);
  font-size: 1.0625rem;
}

.resume {
  margin-top: 1.75rem;
}

.resume-summary {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  padding-left: 0.875rem;
  border-left: 3px solid var(--accent);
}

.resume-summary span {
  color: var(--on-table-muted);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.75rem;
}

.resume .actions {
  margin-top: 0;
}

.actions .btn {
  flex: 1 1 10rem;
}

.fan {
  position: relative;
  height: 200px;
  order: -1;
}

.fan-card {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 108px;
  height: 152px;
  margin-left: -54px;
  --corner-x: 9px;
  --corner-y: 8px;
  background-color: var(--card);
  border-radius: 9px;
  box-shadow: var(--shadow-card);
  color: var(--ink);
  transform-origin: 50% 120%;
  transform: rotate(calc((var(--i) - 1.5) * 13deg));
  animation: deal 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: calc(var(--i) * 70ms);
}

@keyframes deal {
  from {
    transform: rotate(0deg) translateY(16px);
    opacity: 0;
  }
}

.corner {
  position: absolute;
  top: var(--corner-y);
  left: var(--corner-x);
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 0.95;
}

.corner-bottom {
  top: auto;
  left: auto;
  bottom: var(--corner-y);
  right: var(--corner-x);
  transform: rotate(180deg);
}

.pip {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 3rem;
}

.red {
  color: var(--red);
}

.rules {
  padding: 1.25rem;
  border-radius: 14px;
  border: 1px solid var(--panel-edge);
  background-color: var(--panel-solid);
}

.rules h2 {
  font-size: 1.375rem;
}

.rules-lede {
  margin-top: 0.375rem;
  color: var(--on-table-muted);
  max-width: 36em;
}

.rules-grid {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem 1.5rem;
  margin-top: 1.25rem;
}

.rule {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.rule-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.rule-name {
  font-weight: 600;
}

.rule-points {
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

@media (max-width: 767px) {
  .hero {
    gap: 1.25rem;
    padding-bottom: 1.5rem;
  }

  .hero-copy {
    text-align: center;
  }

  h1 {
    font-size: 2.5rem;
  }

  .hero-lede {
    margin: 0.625rem auto 0;
    font-size: 1rem;
  }

  .fan {
    height: 150px;
  }

  .fan-card {
    width: 82px;
    height: 116px;
    margin-left: -41px;
    border-radius: 7px;
    --corner-x: 7px;
    --corner-y: 6px;
  }

  .corner {
    font-size: 1rem;
  }

  .pip {
    font-size: 2.25rem;
  }

  .resume {
    margin-top: 1.5rem;
    padding: 1rem;
    border-radius: 14px;
    background-color: var(--panel-solid);
    border: 1px solid var(--panel-edge);
    text-align: left;
  }

  .resume-summary {
    padding-left: 0;
    border-left: none;
  }

  .resume-summary strong {
    font-size: 1.125rem;
  }

  .resume .actions {
    display: grid;
    gap: 0.75rem;
  }
}

@media (min-width: 560px) {
  .rules-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 900px) {
  .hero {
    grid-template-columns: 1.2fr 1fr;
    padding: 2rem 0 4rem;
  }

  .fan {
    order: 0;
    height: 300px;
  }

  .fan-card {
    width: 148px;
    height: 208px;
    margin-left: -74px;
    border-radius: 12px;
    --corner-x: 12px;
    --corner-y: 10px;
  }

  .corner {
    font-size: 1.625rem;
  }

  .pip {
    font-size: 4rem;
  }

  .actions .btn {
    flex: 0 0 auto;
    min-width: 11rem;
  }

  .rules-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
